import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { StreamingPlaySourceMonth } from 'fym-shared/src/music24/streamingSections';
import { TypedMemo } from '../../../../common/types';
import { playSourceMonthBars } from '../../periodSections';
import { formatCompact, formatMonth } from '../../streamingFormat';
import { playSourcesMessages as messages } from '../../playSourcesMessages';
import { StreamingBlock } from '../../parts/StreamingBlock';
import { PLAY_SOURCE_TILE_COLORS, PLAY_SOURCE_TILE_LABELS } from './PlaySourceTiles';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import shared from '../../Streaming.module.scss';
/* eslint-enable css-modules/no-unused-class */
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the period sections; each part uses its own slice of it */
import styles from '../../StreamingPeriod.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PlaySourceMonthBarsProps {
  byMonth: readonly StreamingPlaySourceMonth[];
}

/** "By month" (design streaming-sections b1): one stacked bar per month, by group. */
const PlaySourceMonthBarsComponent: React.FC<PlaySourceMonthBarsProps> = ({ byMonth }) => {
  const intl = useIntl();
  const bars = useMemo(() => playSourceMonthBars(byMonth), [byMonth]);
  const legend = useMemo(
    () =>
      (Object.keys(PLAY_SOURCE_TILE_LABELS) as Array<keyof typeof PLAY_SOURCE_TILE_LABELS>).map((key) => (
        <span key={key}>
          <span className={styles.swatch} style={{ background: PLAY_SOURCE_TILE_COLORS[key] }} />
          {intl.formatMessage(PLAY_SOURCE_TILE_LABELS[key])}
        </span>
      )),
    [intl]
  );

  return (
    <StreamingBlock
      title={intl.formatMessage(messages.byMonth)}
      meta={intl.formatMessage(messages.byMonthMeta)}
      testId="streaming-playSources-months"
    >
      <div className={styles.stackBars} role="list">
        {bars.map((bar) => (
          <div key={bar.month} className={styles.stackBar} role="listitem">
            <b>{formatCompact(intl, bar.total)}</b>
            <span className={styles.stackColumn} style={{ height: `${Math.round(bar.height * 80)}%` }}>
              {bar.parts.map((part) => (
                <span
                  key={part.key}
                  className={styles.stackPart}
                  style={{ height: `${Math.round(part.share * 100)}%`, background: PLAY_SOURCE_TILE_COLORS[part.key] }}
                />
              ))}
            </span>
            {formatMonth(intl, bar.month)}
          </div>
        ))}
      </div>
      <div className={styles.legend}>{legend}</div>
      <p className={shared.footnote}>{intl.formatMessage(messages.groupsNote)}</p>
    </StreamingBlock>
  );
};

export const PlaySourceMonthBars = TypedMemo(PlaySourceMonthBarsComponent);
